import React from 'react';
import { Tooltip, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { BookmarkBorderOutlined, Bookmark as BookmarkIcon } from '@mui/icons-material';
import {
  useCreateTracker,
  useDeleteTracker,
  useTrackedItems,
  isItemTracked,
} from '../../stores/trackedItems';
import {
  useCreateProductTracker,
  useDeleteProductTrackerByProduct,
  useTrackedProducts,
  isProductTracked,
} from '../../stores/trackedProducts';

interface TrackActionProps {
  item: any;
  sessionToken?: string;
  size?: 'small' | 'medium' | 'large';
  sx?: any;
  onTrackChange?: (tracked: boolean) => void;
}

export default function TrackAction({
  item,
  sessionToken,
  size = 'small',
  sx = {},
  onTrackChange,
}: TrackActionProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: trackedItems = [] } = useTrackedItems();
  const { data: trackedProducts = [] } = useTrackedProducts();
  const createItemTracker = useCreateTracker();
  const deleteItemTracker = useDeleteTracker();
  const createProductTracker = useCreateProductTracker();
  const deleteProductTracker = useDeleteProductTrackerByProduct();

  const isTracked = item.is_product
    ? isProductTracked(trackedProducts, Number(item.id))
    : isItemTracked(trackedItems, Number(item.id));

  const handleTrackToggle = async () => {
    if (!sessionToken) {
      toast.error(t('auth.mustLoginToTrack'));
      return;
    }

    try {
      if (item.is_product) {
        if (isTracked) {
          await deleteProductTracker.mutateAsync({ product_id: Number(item.id) });
        } else {
          await createProductTracker.mutateAsync({ product_id: Number(item.id) });
        }
      } else {
        if (isTracked) {
          // Find the tracker to get its ID
          const tracker = trackedItems.find((t) => Number(t.item_id) === Number(item.id));
          if (tracker) {
            await deleteItemTracker.mutateAsync({ id: tracker.id });
          }
        } else {
          await createItemTracker.mutateAsync({ item_id: Number(item.id) });
        }
      }

      // Invalidate and refetch queries
      await queryClient.invalidateQueries({ queryKey: ['trackedItems'] });
      await queryClient.invalidateQueries({ queryKey: ['trackedProducts'] });
      await queryClient.refetchQueries({ queryKey: ['trackedItems'] });
      await queryClient.refetchQueries({ queryKey: ['trackedProducts'] });

      // Call the callback if provided
      if (onTrackChange) {
        onTrackChange(!isTracked);
      }
    } catch (error) {
      console.error('Error toggling track:', error);
      toast.error(t('search.errors.trackToggleFailed'));
    }
  };

  return (
    <Tooltip title={isTracked ? t('common.untrack') : t('common.track')}>
      <IconButton
        onClick={handleTrackToggle}
        size={size}
        sx={{
          width: '32px',
          height: '32px',
          backgroundColor: isTracked ? '#FFD700' : 'transparent',
          border: (theme) => `1px solid ${theme.palette.secondary.main}33`,
          '&:hover': {
            backgroundColor: isTracked ? '#FFD700' : 'rgba(0, 0, 0, 0.04)',
            border: (theme) => `1px solid ${theme.palette.secondary.main}`,
          },
          ...sx,
        }}
      >
        {isTracked ? (
          <BookmarkIcon sx={{ color: '#000000' }} />
        ) : (
          <BookmarkBorderOutlined sx={{ color: (theme) => theme.palette.text.secondary }} />
        )}
      </IconButton>
    </Tooltip>
  );
}
